/**
 * Contratos de domínio — app de seletividade alimentar (local-first, PT-BR).
 *
 * Fonte da verdade do modelo de dados. Os nomes de campos seguem o escopo do MVP
 * (snake_case em PT-BR) de propósito — não renomeie sem atualizar o schema.
 *
 * Decisão de arquitetura (não negociável, vinda do escopo):
 *  - `origem` da Oferta é gravada SEMPRE (higiene de dados).
 *  - `propriedades sensoriais` são dados estruturados, não texto livre.
 *  - a `escada` é um enum ORDENADO desde já (P1/P2 dependem disso).
 */

// ──────────────────────────────────────────────────────────────────────────
// Enums ordenados / unions
// ──────────────────────────────────────────────────────────────────────────

/**
 * Escada de aproximação (inspirada na terapia alimentar SOS).
 * É um enum ORDENADO: o valor numérico É o passo. Compare com < e >.
 * Subir de 0 → 3 já é progresso real — trate como vitória, nunca como "ainda não comeu".
 */
export enum PassoEscada {
  NaoToleraNaMesa = 0, // não tolera o alimento na mesa
  ToleraNaMesa = 1, // tolera na mesa
  ToleraNoPrato = 2, // tolera no prato dele
  Toca = 3, // toca
  Cheira = 4, // cheira
  LevaABoca = 5, // leva à boca / lambe
  Prova = 6, // prova
  MastigaECospe = 7, // mastiga e cospe
  ComeUmPouco = 8, // come um pouco
  ComeAPorcao = 9, // come a porção
}

/** Rótulos em sentence case para a UI. Indexável por PassoEscada. */
export const PASSO_ESCADA_LABEL: Record<PassoEscada, string> = {
  [PassoEscada.NaoToleraNaMesa]: "não tolera na mesa",
  [PassoEscada.ToleraNaMesa]: "tolera na mesa",
  [PassoEscada.ToleraNoPrato]: "tolera no prato",
  [PassoEscada.Toca]: "toca",
  [PassoEscada.Cheira]: "cheira",
  [PassoEscada.LevaABoca]: "leva à boca / lambe",
  [PassoEscada.Prova]: "prova",
  [PassoEscada.MastigaECospe]: "mastiga e cospe",
  [PassoEscada.ComeUmPouco]: "come um pouco",
  [PassoEscada.ComeAPorcao]: "come a porção",
};

export const ESCADA_MIN = PassoEscada.NaoToleraNaMesa; // 0
export const ESCADA_MAX = PassoEscada.ComeAPorcao; // 9

/** Status do alimento. NÃO é nota — é situação. Nunca renderize como erro/vermelho. */
export type StatusAlimento = "seguro" | "em_teste" | "recusado" | "nao_oferecido";

/**
 * Quem originou o registro. Gravado automaticamente.
 * `profissional` = sessão de terapia (TO/fono/nutri…), registrada pelo cuidador
 * para confrontar com o que acontece em casa.
 */
export type Origem = "cuidador" | "crianca" | "profissional";

export const ORIGEM_LABEL: Record<Origem, string> = {
  cuidador: "cuidador",
  crianca: "criança",
  profissional: "terapia",
};

/** Especialidade do profissional na sessão de terapia (opcional). */
export type EspecialidadeProf = "to" | "fono" | "nutri" | "psico" | "medico" | "outro";

export const ESPECIALIDADE_LABEL: Record<EspecialidadeProf, string> = {
  to: "terapeuta ocupacional",
  fono: "fonoaudiólogo(a)",
  nutri: "nutricionista",
  psico: "psicólogo(a)",
  medico: "médico(a)",
  outro: "outro profissional",
};

/** Direção da tendência na escada ao longo das ofertas. */
export type Tendencia = "subindo" | "estavel" | "caindo" | "poucos_dados";

/** Sistema de CAA que a criança já usa (alinhamento futuro — P2.4). */
export type SistemaCAA = "nenhum" | "PECS" | "outro";

export type Refeicao = "cafe" | "almoco" | "lanche" | "jantar" | "outro";

export const REFEICAO_LABEL: Record<Refeicao, string> = {
  cafe: "café da manhã",
  almoco: "almoço",
  lanche: "lanche",
  jantar: "jantar",
  outro: "outro",
};

/**
 * Grupo alimentar — base nutricional para o pai montar a refeição com os
 * essenciais (uma proteína, uma fibra, uma fruta…). Opcional por alimento.
 */
export type GrupoAlimentar =
  | "proteina"
  | "carboidrato"
  | "fibra"
  | "fruta"
  | "laticinio"
  | "outro";

export const GRUPO_LABEL: Record<GrupoAlimentar, string> = {
  proteina: "proteína",
  carboidrato: "carboidrato",
  fibra: "fibra / legume",
  fruta: "fruta",
  laticinio: "laticínio",
  outro: "outro",
};

/** Ordem de exibição dos grupos (essenciais primeiro). */
export const GRUPOS_ORDEM: GrupoAlimentar[] = [
  "proteina",
  "carboidrato",
  "fibra",
  "fruta",
  "laticinio",
  "outro",
];

/** Humor antes da oferta (não-punitivo, escala curta). */
export type HumorAntes = "tranquila" | "neutra" | "agitada" | "cansada";

// ──────────────────────────────────────────────────────────────────────────
// Propriedades sensoriais (dados estruturados, não texto livre)
// ──────────────────────────────────────────────────────────────────────────

/**
 * As 5 dimensões sensoriais do escopo. Mantidas abertas (string) para o MVP,
 * mas estruturadas por dimensão — base para "alimentos parecidos" (P1.1) e
 * para a base aberta de perfis sensoriais (P2.3).
 */
export interface PropriedadesSensoriais {
  textura: string | null; // ex.: "macia", "crocante", "purê"
  temperatura: string | null; // ex.: "gelada", "morna", "ambiente"
  cor: string | null; // ex.: "vermelha", "verde"
  forma_apresentacao: string | null; // ex.: "redonda", "em cubos", "inteira"
  cheiro: string | null; // ex.: "doce", "neutro", "forte"
}

// ──────────────────────────────────────────────────────────────────────────
// Entidades
// ──────────────────────────────────────────────────────────────────────────

export interface Crianca {
  id: string;
  apelido: string; // como a família chama (usado na saudação e no modo criança)
  nome_completo: string | null; // nome completo do paciente
  data_nascimento: string | null; // ISO date "YYYY-MM-DD"
  foto: string | null; // referência local (caminho/blob id), nunca URL remota
  sistema_caa: SistemaCAA;
  // responsável pelo paciente (cadastro básico — fica só no aparelho)
  responsavel_nome: string | null;
  responsavel_cpf: string | null;
  // recomendações de preparo/embalagem específicas da criança (ex.: "embalagem com
  // divisórias; feijão sempre separado"). Viajam junto no pedido (WhatsApp/PDF).
  recomendacoes: string | null;
  criado_em: string; // ISO 8601
}

export interface Alimento {
  id: string;
  nome: string;
  fotos: string[]; // refs locais; a primeira é a capa
  marca_produto: string | null; // marca/corte/formato específico (opcional, mas valioso)
  categoria: string | null;
  grupo: GrupoAlimentar | null; // grupo nutricional (proteína, fibra…) — opcional
  sensorial: PropriedadesSensoriais;
  status: StatusAlimento;
  observacoes: string | null;
  arquivado: boolean; // P0.1: arquivar em vez de deletar
  criado_em: string; // ISO 8601
}

/**
 * Evento de exposição. Captura mínima na hora; campos de contexto são todos
 * opcionais e NUNCA bloqueiam o salvar (registro em ≤10s — P0.2).
 */
export interface Oferta {
  id: string;
  alimento_id: string;
  crianca_id: string;
  data_hora: string; // ISO 8601
  passo_escada: PassoEscada; // 0–9 (obrigatório — é a "reação")
  porcao_comida_pct: number | null; // 0–100, opcional
  porcao_oferecida: string | null; // descrição livre opcional
  // contexto opcional:
  refeicao: Refeicao | null;
  local: string | null;
  quem_ofereceu: string | null;
  quem_presente: string[]; // pode ser []
  humor_antes: HumorAntes | null;
  // higiene de dados:
  origem: Origem; // gravado automaticamente
  confirmado: boolean; // ofertas de origem "crianca" nascem pendentes (false)
  notas: string | null;
  // contexto de terapia (só quando origem === "profissional"; opcionais):
  profissional_nome?: string | null; // quem conduziu a sessão
  profissional_especialidade?: EspecialidadeProf | null;
}

// ──────────────────────────────────────────────────────────────────────────
// Derivados por alimento (CALCULADOS, não persistidos como fonte da verdade)
// ──────────────────────────────────────────────────────────────────────────

export interface DerivadosAlimento {
  alimento_id: string;
  n_ofertas: number;
  ultima_oferta: string | null; // ISO 8601
  passo_atual: PassoEscada | null; // passo da oferta mais recente
  passo_maximo: PassoEscada | null; // maior passo já atingido
  tendencia: Tendencia;
  dias_desde_ultima: number | null; // alimenta o lembrete de re-exposição (P0.6)
}

// ──────────────────────────────────────────────────────────────────────────
// Configurações (local-first, sem conta)
// ──────────────────────────────────────────────────────────────────────────

export interface Config {
  crianca_ativa_id: string | null;
  // cadência do lembrete de re-exposição, em dias (P0.6)
  reexposicao_dias: number; // padrão sugerido: 5
  // cadência alvo do modo escolha (consistência ajuda a criança) — informativo
  modo_escolha_por_semana: number; // padrão sugerido: 2
  // quantos itens a criança escolhe na escolha livre, por tipo de refeição.
  // ao atingir essa quantidade, a escolha encerra sozinha e volta pro cuidador.
  escolha_qtd_por_refeicao: Record<Refeicao, number>;
  // número fixo de WhatsApp pra enviar o pedido do prato (só dígitos com DDI/DDD).
  // null = abre o WhatsApp sem destinatário (o usuário escolhe o contato).
  whatsapp_destino: string | null;

  // ── Compasso (módulo de musicoterapia) ──────────────────────────────────
  // cadência do lembrete de retomar experiências em exploração, em dias (P0.6).
  musica_rotina_dias: number; // padrão sugerido: 5
  // quantas opções aparecem no modo escolha musical da criança (2–3).
  musica_escolha_qtd: number; // padrão sugerido: 2

  // ── Elo (módulo de integração interdisciplinar) ─────────────────────────
  // cadência do lembrete: avisa quando uma disciplina fica sem nota há N dias.
  elo_lembrete_dias: number; // padrão sugerido: 14

  // ── Gingado (módulo de psicomotricidade) ────────────────────────────────
  // cadência do lembrete de retomar atividades em exploração, em dias (P0.6).
  gingado_rotina_dias: number; // padrão sugerido: 5
  // quantas opções aparecem no modo escolha corporal da criança (2–3).
  gingado_escolha_qtd: number; // padrão sugerido: 2

  // ── Perfil profissional (início simples de roles/perfis) ────────────────
  // OFF por padrão: o app é family-first (1 paciente). Quando true, este aparelho
  // é de um profissional/clínica → libera o Painel do profissional, a gestão de
  // vários pacientes e o acesso à Agenda. Sem auth ainda — é um flag local.
  perfil_profissional: boolean; // padrão: false

  // ── Agenda / gestão de clínica (camada transversal — ADR-22, Fase 1) ─────
  // OFF por padrão: enquanto false, a Agenda não aparece e nada muda no app.
  // É a camada "contratável" — se não ativar, os 4 módulos seguem idênticos.
  agenda_ativa: boolean; // padrão: false
  // id da clínica solo (de 1) criada na 1ª ativação; null enquanto inativa.
  clinica_id: string | null;
  // provedor do calendário (Fase 1 só 'nativo'; 'google' é backlog/Fase 2).
  agenda_provedor: ProvedorAgenda; // padrão: "nativo"
}

/** Provedor do calendário da agenda. 'google' é backlog (Fase 2). */
export type ProvedorAgenda = "nativo" | "google";

export const CONFIG_PADRAO: Config = {
  crianca_ativa_id: null,
  reexposicao_dias: 5,
  modo_escolha_por_semana: 2,
  // almoço/jantar pedem prato montado (proteína + carbo + fibra); café/lanche menos
  escolha_qtd_por_refeicao: {
    cafe: 2,
    almoco: 3,
    lanche: 1,
    jantar: 3,
    outro: 2,
  },
  whatsapp_destino: null,
  musica_rotina_dias: 5,
  musica_escolha_qtd: 2,
  elo_lembrete_dias: 14,
  gingado_rotina_dias: 5,
  gingado_escolha_qtd: 2,
  // Family-first por padrão: perfil profissional desligado.
  perfil_profissional: false,
  // Agenda nasce desligada: zero impacto até o profissional ativar.
  agenda_ativa: false,
  clinica_id: null,
  agenda_provedor: "nativo",
};
